import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Switch,
} from 'antd';
import dayjs from 'dayjs';

import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import DurationInput from '@totem/components/ticketing/details/tasks/durationInput';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import {
  Resources,
  Task,
  TASK_TYPE_GROUP,
  TASK_TYPE_MILESTONE,
  TASK_TYPE_TASK,
} from '@totem/components/ticketing/ticketing';
import { TaskCreateExtras } from '@totem/components/ticketing/types';
import { getNextRootPosition } from '@totem/components/ticketing/utilities';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { TICKET_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
type Props = {
  resources: Resources;
  tasks: Task[];
};
const TaskEntryDialog = ({ resources, tasks }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { ticket, onAction, action, actionExtras } = useContext(TicketContext);
  const [isSending, setIsSending] = useState(false);
  const [taskData, setTaskData] = useState<Task>({
    id: EMPTY_ID,
    type: TASK_TYPE_TASK,
    number: 0,
    parent: 0,
    position: getNextRootPosition(ticket),
    name: '',
    description: '',
    expectedStartDate: null,
    expectedDuration: {
      days: 0,
      hours: 0,
      minutes: 0,
    },
    actualStartDate: null,
    actualDuration: {
      days: 0,
      hours: 0,
      minutes: 0,
    },
    actualEndDate: null,
    dependencies: [],
    labels: [],
    assignedResources: [],
    status: 'PENDING',
    budgetedHours: 0.0,
    actualHours: 0.0,
    billingType: '',
    isBillingMilestone: false,
    internalOnly: false,
    isDeleted: false,
    createdByType: 'USER',
    createdByName: userProfile.email,
    createdById: userProfile.id,
    createdAt: dayjs().toISOString(),
  });
  const [form] = Form.useForm();
  const getTaskLabel = (taskId: number): string => {
    const task = tasks.find((tsk) => tsk.number === taskId);
    return task ? `${task.number}: ${task.name}` : `${taskId}`;
  };
  useEffect(() => {
    if (isNotNull(actionExtras)) {
      try {
        const extras: TaskCreateExtras = JSON.parse(actionExtras);
        if (isNotNull(extras.editTask) && isNotNull(taskData)) {
          setTaskData(extras.editTask);

          form.setFieldsValue({
            name: extras.editTask.name,
            type: extras.editTask.type,
            description: extras.editTask.description,
            expectedStartDate: isNotNull(extras.editTask.expectedStartDate)
              ? dayjs(extras.editTask.expectedStartDate)
              : null,
            expectedDuration: extras.editTask.expectedDuration,
            dependencies: extras.editTask.dependencies.map((chk) => ({
              value: chk.taskId,
              label: getTaskLabel(chk.taskId),
            })),
            resourceId: extras.editTask.assignedResources, // Note: form field name 'resourceId', taskData 'assignedResources'
            budgetedHours: extras.editTask.budgetedHours,
            isBillingMilestone: extras.editTask.isBillingMilestone,
            internalOnly: extras.editTask.internalOnly,
          });
        }
      } catch {
        onAction('', '');
      }
    }
  }, [actionExtras]);
  const ticketId =
    isNotNull(ticket) && isNotNull(ticket.ticket) ? ticket.ticket.id : '';
  const sendTask = useCallback(
    async (paramTicketId: string, payload: Task) => {
      if (!isSending) {
        fetch(`${TICKET_ENDPOINT}/${paramTicketId}/tasks`, {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
          body: JSON.stringify(payload),
        }).then((res) => {
          setIsSending(false);
          const success = CheckResponseShowError(res);
          if (success) {
            form.resetFields();

            onAction('refresh', '');
          }
        });
      }
    }, // End async
    [],
  );
  const handleSubmit = () => {
    form.validateFields().then(() => {
      sendTask(ticketId, taskData);
    });
  };
  return (
    <Modal
      open={action === 'show-task-create'}
      title=""
      onCancel={() => {
        form.resetFields();

        onAction('', '');
      }}
      onOk={handleSubmit}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            form.resetFields();

            onAction('', '');
          }}
        >
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          Submit
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item label="Type" name="type" initialValue={taskData.type}>
          <Radio.Group
            value={taskData.type}
            buttonStyle={'solid'}
            onChange={(evt) =>
              setTaskData((prevState) => ({
                ...prevState,
                type: evt.target.value,
              }))
            }
          >
            <Radio.Button value={TASK_TYPE_GROUP}>Group</Radio.Button>
            <Radio.Button value={TASK_TYPE_TASK}>Task</Radio.Button>
            <Radio.Button value={TASK_TYPE_MILESTONE}>Milestone</Radio.Button>
          </Radio.Group>
        </Form.Item>
        {taskData.type === TASK_TYPE_MILESTONE && (
          <Form.Item
            label="Billing Milestone"
            name="isBillingMilestone"
            initialValue={taskData.isBillingMilestone}
          >
            <Switch
              checked={taskData.isBillingMilestone}
              onChange={(isChecked) =>
                setTaskData((prevState) => ({
                  ...prevState,
                  isBillingMilestone: isChecked,
                }))
              }
            />
          </Form.Item>
        )}
        {permissions.internal.hasPermissions(userProfile) && (
          <Form.Item
            label="Internal Only"
            name="internalOnly"
            initialValue={taskData.internalOnly}
          >
            <Switch
              checked={taskData.internalOnly}
              onChange={(isChecked) =>
                setTaskData((prevState) => ({
                  ...prevState,
                  internalOnly: isChecked,
                }))
              }
            />
          </Form.Item>
        )}
        <Form.Item
          label="Name"
          name="name"
          initialValue={taskData.name}
          rules={[{ required: true, message: 'Please enter task name' }]}
        >
          <Input
            value={taskData.name}
            onChange={(event) =>
              setTaskData((prevState) => ({
                ...prevState,
                name: event.target.value,
              }))
            }
          />
        </Form.Item>
        <Form.Item
          label="Description"
          name="description"
          initialValue={taskData.description}
        >
          <Input.TextArea
            rows={3}
            value={taskData.description}
            onChange={(event) =>
              setTaskData((prevState) => ({
                ...prevState,
                description: event.target.value,
              }))
            }
          />
        </Form.Item>
        <Form.Item
          label="Scheduled Start Date"
          name="expectedStartDate"
          initialValue={
            isNotNull(taskData.expectedStartDate)
              ? dayjs(taskData.expectedStartDate)
              : dayjs()
          }
        >
          <DatePicker
            style={{ width: '100%' }}
            value={
              isNotNull(taskData.expectedStartDate)
                ? dayjs(taskData.expectedStartDate)
                : dayjs()
            }
            onChange={(newValue) =>
              setTaskData((prevState) => ({
                ...prevState,
                expectedStartDate: isNotNull(newValue)
                  ? newValue.toISOString()
                  : null,
              }))
            }
          />
        </Form.Item>
        {taskData.type === TASK_TYPE_TASK && (
          <Form.Item
            label="Planned Duration"
            name="expectedDuration"
            initialValue={taskData.expectedDuration}
          >
            <DurationInput
              value={taskData.expectedDuration}
              onChange={(newDuration) =>
                setTaskData((prevState) => ({
                  ...prevState,
                  expectedDuration: newDuration,
                }))
              }
            />
          </Form.Item>
        )}
        <Form.Item
          label="Dependancies"
          name="dependencies"
          initialValue={taskData.dependencies.map((chk) => ({
            value: chk.taskId,
            label: getTaskLabel(chk.taskId),
          }))}
          rules={[
            {
              required: taskData.type === TASK_TYPE_MILESTONE,
              message: 'Please enter at least one dependency',
            },
          ]}
        >
          <Select
            placeholder="Select dependancies"
            labelInValue
            value={taskData.dependencies.map((chk) => ({
              value: chk.taskId,
              label: getTaskLabel(chk.taskId),
            }))}
            mode={'multiple'}
            onChange={(values) =>
              setTaskData((prevState) => ({
                ...prevState,
                dependencies: values.map((val) => ({
                  taskId: val.value,
                  relationshipType: 'FS',
                })),
              }))
            }
          >
            {tasks.map((tsk) => (
              <Select.Option key={tsk.number} value={tsk.number}>
                {`${tsk.number}: ${tsk.name}`}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Resource"
          name="resourceId"
          initialValue={taskData.assignedResources}
        >
          <Select
            placeholder="Select resource"
            value={taskData.assignedResources}
            mode={'multiple'}
            onChange={(value) =>
              setTaskData((prevState) => ({
                ...prevState,
                assignedResources: value,
              }))
            }
          >
            {resources.map((res) => (
              <Select.Option key={res.id} value={res.id}>
                {res.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Budgeted Hours"
          name="budgetedHours"
          initialValue={taskData.budgetedHours}
        >
          <InputNumber
            min={0}
            step={0.25}
            style={{ width: '100%' }}
            value={taskData.budgetedHours}
            onChange={(value) =>
              setTaskData((prevState) => ({
                ...prevState,
                budgetedHours: value,
              }))
            }
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
export default TaskEntryDialog;
